import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LanguageToggle } from '@/components/LanguageToggle';
import { toast } from 'sonner';
import { FilmSlate, Eye, EyeSlash } from '@phosphor-icons/react';

export default function LoginPage() {
  const { login } = useAuth();
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('errorOccurred'));
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('welcomeBack') + '!');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#171717] items-center justify-center p-12 relative">
        <div className="absolute top-6 right-6">
          <LanguageToggle />
        </div>
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#FF5D01] rounded-lg flex items-center justify-center">
              <FilmSlate size={28} weight="fill" className="text-white" />
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">Script Visual</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            {language === 'es' 
              ? 'Transforma tus guiones en storyboards visuales'
              : 'Transform your scripts into visual storyboards'}
          </h1>
          <p className="text-[#A1A1AA] text-lg">
            {language === 'es'
              ? 'Sube tu guión de YouTube y genera automáticamente visuales escena por escena con sugerencias de IA y recursos libres de derechos.'
              : 'Upload your YouTube script and automatically generate scene-by-scene visuals with AI-powered suggestions and copyright-free assets.'}
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="bg-[#262626] p-4 rounded-lg border border-[#333333]">
              <div className="text-[#FF5D01] text-2xl font-bold">15-16</div>
              <div className="text-[#A1A1AA] text-sm">{language === 'es' ? 'Min videos soportados' : 'Min videos supported'}</div>
            </div>
            <div className="bg-[#262626] p-4 rounded-lg border border-[#333333]">
              <div className="text-[#FF5D01] text-2xl font-bold">100+</div>
              <div className="text-[#A1A1AA] text-sm">{language === 'es' ? 'Escenas por proyecto' : 'Scenes per project'}</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 lg:hidden">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-[#FF5D01] rounded-lg flex items-center justify-center">
              <FilmSlate size={24} weight="fill" className="text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Script Visual</span>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">{t('welcomeBack')}</h2>
          <p className="text-[#A1A1AA] mb-8">{t('signInContinue')}</p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#F8FAFC]">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#171717] border-[#333333] text-white placeholder:text-[#A1A1AA] focus:ring-[#FF5D01] focus:border-[#FF5D01]"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#F8FAFC]">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('language') === 'es' ? 'Ingresa tu contraseña' : 'Enter your password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#171717] border-[#333333] text-white placeholder:text-[#A1A1AA] focus:ring-[#FF5D01] focus:border-[#FF5D01] pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF5D01] hover:bg-[#e65400] text-white font-medium py-2.5 rounded-md transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? t('loading') : t('login')}
            </Button>
          </form>
          
          <p className="mt-6 text-center text-[#A1A1AA]">
            {t('noAccount')}{' '}
            <Link to="/register" className="text-[#FF5D01] hover:underline font-medium" data-testid="go-to-register-link">
              {t('createAccount')}
            </Link>
          </p>
          
          <p className="mt-4 text-center">
            <a 
              href="/guide.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-white text-sm transition-colors"
              data-testid="login-user-guide-link"
            >
              {t('needHelp')} {t('userGuide')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
